import { useCallback, useMemo, useState } from "react";

import {
	type Money,
	moneyFromMajorFloat,
	percentOf,
	toMajorFloatAmount,
	toMinorUnits,
	zeroMoney,
} from "@performyard/money";

import { type DecisionRow } from "../../../types";
import { type IncreaseValues } from "../types";

export const useIncreaseValues = (baseDecisionRowsByManagerId: Record<string, DecisionRow[]>) => {
	const [draftOverrides, setDraftOverrides] = useState<IncreaseValues>({});

	const salaryById = useMemo(() => {
		const map = new Map<string, Money>();
		for (const rows of Object.values(baseDecisionRowsByManagerId)) {
			for (const row of rows) {
				map.set(row._id, row.current_salary);
			}
		}
		return map;
	}, [baseDecisionRowsByManagerId]);

	const increaseValues: IncreaseValues = useMemo(() => {
		const merged: IncreaseValues = {};
		for (const rows of Object.values(baseDecisionRowsByManagerId)) {
			for (const row of rows) {
				if (draftOverrides[row._id]) {
					merged[row._id] = draftOverrides[row._id];
				} else {
					merged[row._id] = {
						percent: row.proposed_increase_percent ?? null,
						// The draft inputs are numeric, so money becomes a number only here, at
						// the input boundary. Everything it is derived from stays exact.
						amount:
							row.proposed_increase_amount != null
								? toMajorFloatAmount(row.proposed_increase_amount)
								: null,
					};
				}
			}
		}
		return merged;
	}, [draftOverrides, baseDecisionRowsByManagerId]);

	const onPercentChange = useCallback(
		(id: string, percent: number | "") => {
			setDraftOverrides((prev) => {
				if (percent === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? zeroMoney();
				// The dollar figure the manager sees and submits. `percentOf` applies the
				// percentage to exact money; the number appears only at the input boundary.
				const amount = toMajorFloatAmount(percentOf(salary, percent));
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	const onAmountChange = useCallback(
		(id: string, amount: number | "") => {
			setDraftOverrides((prev) => {
				if (amount === "") return { ...prev, [id]: { percent: "", amount: "" } };
				const salary = salaryById.get(id) ?? zeroMoney();
				// Ratio taken over exact minor units on both sides, so only the final
				// quantization to two decimals is inexact.
				const salaryMinorUnits = toMinorUnits(salary);
				const percent =
					salaryMinorUnits > 0
						? Math.round((toMinorUnits(moneyFromMajorFloat(amount)) / salaryMinorUnits) * 10000) / 100
						: 0;
				return { ...prev, [id]: { percent, amount } };
			});
		},
		[salaryById]
	);

	return { increaseValues, setDraftOverrides, onPercentChange, onAmountChange };
};
